import {
  storyblokItemFromConfig,
  useGetStoryblokItem,
} from "../../../../hooks/V2/storyblok/useItem"

import ProductInfoSection from "@components/V2/Sections/Info/ProductInfo"
import { type Variant } from "@components/V2/Sections/Info/Variants"
import {
  isConsolidatedPurchasableItem,
  parseConsolidatedPrice,
  useGetConsolidatedItem,
} from "@hooks/V2/storyblok/useConsolidatedItem"
import { getProductSavingsBadge } from "@utils/shop"
import { storyblokLinkToYocoLink } from "@utils/storyblok"
import React, { useState, useCallback } from "react"

const StoryblokProductInfoSection = ({
  blok,
  mobileGridOrder,
}: Storyblok.BlokProps<Storyblok.ProductInfoSection>) => {
  const getConsolidatedItem = useGetConsolidatedItem()
  const [selectedOption, setSelectedOption] = useState(0)
  const getStoryblokItem = useGetStoryblokItem()

  const handleVariantOptionChange = useCallback((id: number) => {
    setSelectedOption(id)
  }, [])

  const selectedVariantOption =
    selectedOption < blok.variants.length ? selectedOption : 0
  const selectedVariant = blok.variants[selectedVariantOption]
  const storyblokItem = getStoryblokItem(selectedVariant.productConfig)
  const config = storyblokItem.content

  // TODO: Investigate why a UUID productConfig can fail to resolve to a product or bundle config.
  const consolidatedItem = config
    ? getConsolidatedItem(storyblokItemFromConfig(storyblokItem.id, config))
    : undefined

  if (!isConsolidatedPurchasableItem(consolidatedItem)) {
    return (
      <div className="col-span-full flex h-96 items-center justify-center">
        {selectedVariant.productConfig.content?.name} is not available at the
        moment
      </div>
    )
  }

  const priceValue = parseConsolidatedPrice(consolidatedItem.price)
  const originalPriceValue = parseConsolidatedPrice(
    consolidatedItem.originalPrice
  )

  const effectiveBadge =
    getProductSavingsBadge(consolidatedItem) ??
    consolidatedItem.badge ??
    undefined

  const variants: Variant[] = []

  for (const [optionIndex, variant] of blok.variants.entries()) {
    const variantItem = getStoryblokItem(variant.productConfig)
    const variantConsolidatedItem = variantItem.content
      ? getConsolidatedItem(
          storyblokItemFromConfig(variantItem.id, variantItem.content)
        )
      : undefined

    if (isConsolidatedPurchasableItem(variantConsolidatedItem)) {
      variants.push({
        id: variant._uid,
        label: variant.label || variantConsolidatedItem.name,
        price: variantConsolidatedItem.price,
        optionIndex,
        reviewSkus: variantConsolidatedItem.reviewSkus,
      })
    }
  }

  return (
    <ProductInfoSection
      mobileGridOrder={mobileGridOrder}
      galleryItems={consolidatedItem.galleryItems}
      productName={consolidatedItem.name}
      showRecommendedProducts={
        consolidatedItem.showRecommendedProducts || false
      }
      recommendedProducts={consolidatedItem.recommendedProducts}
      productDescription={consolidatedItem.description}
      descriptionHeader={consolidatedItem.descriptionHeader}
      price={{
        adornmentText: "R",
        value: priceValue,
        superscriptText: consolidatedItem.originalPrice
          ? `was R${originalPriceValue}`
          : undefined,
        sideScriptText: consolidatedItem.priceSideScript,
        subscriptText: consolidatedItem.priceSubScript,
      }}
      waitlistLink={consolidatedItem.waitlistLink}
      productUrl={storyblokLinkToYocoLink(blok.productUrl)}
      consolidatedProduct={consolidatedItem}
      contentSections={selectedVariant.infoSections || []}
      badge={effectiveBadge}
      tags={selectedVariant.tags}
      variants={variants}
      variantDescription={selectedVariant.description}
      selectedVariantOption={selectedVariantOption}
      onVariantClick={handleVariantOptionChange}
      showRatings={blok.showRatings}
      paymentStrip={
        consolidatedItem.paymentStripDark?.filename ||
        consolidatedItem.paymentStripLight?.filename
      }
    />
  )
}

export default StoryblokProductInfoSection
